import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { Layout } from '@/components/Layout'
import { FilesPage } from '@/pages/FilesPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { SettingsPage } from '@/pages/SettingsPage'
import { PublicDropPage } from '@/pages/PublicDropPage'
import { PublicSharePage } from '@/pages/PublicSharePage'
import { useEffect, useState } from 'react'
import { useAppStore } from '@/stores/appStore'
import { checkSession } from '@/services/auth'
import { LogoMark } from '@/components/Logo'

function Splash({ message }: { message?: string }) {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-5 bg-background">
      <LogoMark size={48} />
      <div className="flex flex-col items-center gap-3">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary" aria-label="Loading" />
        {message && <p className="text-sm text-muted-foreground">{message}</p>}
      </div>
    </div>
  )
}

function LoginPage() {
  useEffect(() => {
    // Check if already logged in
    checkSession().then((session) => {
      if (session.authenticated) {
        window.location.href = '/files'
      } else {
        // Fetch login URL from backend
        const apiBase = import.meta.env.VITE_API_URL || 'https://freeholdapi.ntg.co/api/v1'
        fetch(`${apiBase}/auth/login-url`, {
          credentials: 'include',
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.auth_url) {
              // Redirect to Authentik
              window.location.href = data.auth_url
            } else {
              console.error('No auth_url in response')
              window.location.href = '/login?error=no_auth_url'
            }
          })
          .catch((err) => {
            console.error('Failed to get login URL:', err)
            window.location.href = '/login?error=failed_to_get_url'
          })
      }
    })
  }, [])

  return <Splash message="Redirecting to sign in…" />
}

function CallbackPage() {
  useEffect(() => {
    // Get code and state from URL
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const error = params.get('error')

    if (error) {
      console.error('[Callback] OAuth error:', error)
      window.location.href = '/login?error=' + error
      return
    }

    if (!code) {
      console.error('[Callback] Missing code')
      window.location.href = '/login?error=missing_code'
      return
    }

    // Exchange code with backend
    const apiBase = import.meta.env.VITE_API_URL || 'https://freeholdapi.ntg.co/api/v1'
    const exchangeUrl = `${apiBase}/auth/exchange?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state || '')}`
    
    fetch(exchangeUrl, {
      credentials: 'include', // Send cookies
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Exchange failed')
        }
        return res.json()
      })
      .then(() => {
        // Success - redirect to files
        window.location.href = '/files'
      })
      .catch((err) => {
        console.error('[Callback] Exchange error:', err)
        window.location.href = '/login?error=exchange_failed'
      })
  }, [])

  return <Splash message="Completing sign in…" />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)
  const { fetchUser } = useAppStore()

  useEffect(() => {
    checkSession().then((session) => {
      setLoading(false)
      setAuthenticated(session.authenticated)
      if (session.authenticated) {
        // Fetch full user data from /me endpoint
        fetchUser()
      }
    })
  }, [fetchUser])

  if (loading) {
    return <Splash />
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/drop/:token" element={<PublicDropPage />} />
        <Route path="/share/:token" element={<PublicSharePage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/files" replace />} />
          <Route path="files/*" element={<FilesPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
      </Routes>
      <Toaster />
    </BrowserRouter>
  )
}

export default App
